import React, { ReactElement } from 'react';
import type { NextPage } from 'next';
import Error from 'next/error';

import { logger } from '@lib/logger';
import { renderComponents, getSlug } from '@lib/utils';
import {
  blogController,
  globalContentController,
  pageController,
  productController,
} from '@controllers/index';
import { rowFactory } from '@factories/index';

import { PageType } from '@lib/types';
import useBasketContext from '@hooks/useBasketContext';
import usePostcodeContext from '@hooks/usePostcodeContext';
import { StickyCheckoutFooter } from '@components/Molecules/StickyCheckoutFooter/StickyCheckoutFooter';
import { breakpoints } from '@theme/breakpoints';
import { ArchiveGrid } from '@components/Organisms/ArchiveGrid/ArchiveGrid';
import { reviewHeadingStyles } from '@lib/styles/ReviewHeadingStyles';
import { handleConditionalExecution } from '@lib/utils/conditionalExecution';

export const getServerSideProps = async ({ resolvedUrl }: any) => {
  const slug = getSlug({ type: PageType.COVERAGE_PAGE, resolvedUrl });

  let location: any = {};
  let page: any = {};
  let pages: any = [];
  let products: any = [];
  let footerData: any = [];
  let promoData: any = [];
  let communityPosts = [];
  let blogPosts = [];
  let productSettings: any = [];

  try {
    location = await pageController.getCoveragePageBySlug({ slug });
    page = await pageController.getSingleCoveragePageTemplate({ location });
    pages = await pageController.getPages();
    communityPosts = await blogController.getCommunityPostsByCoveragePage({ slug });
    blogPosts = await blogController.getBlogPostsByCoveragePage({ slug });
    products = await productController.getProducts();
    footerData = await globalContentController.getFooter();
    promoData = await globalContentController.getPromoData();
    productSettings = await pageController.getProductSettingsPage();
  } catch (err) {
    logger.error(err);
  }

  if (!location) {
    return {
      redirect: {
        destination: '/coverage',
        permanent: true,
      },
    };
  }

  const rows = pageController.getRows({ page });

  const headerData = pageController.getHeaderData({ page, pages });
  const metadata = pageController.getMetadata({ page: location });
  const featureList = pageController.getFeatureList({ page });

  return {
    props: {
      metadata,
      promoData,
      headerData,
      footerData,
      featureList: { list: featureList },
      data: {
        page,
        products,
        rows,
        communityPosts,
        location,
        blogPosts,
        productSettings,
      },
    },
  };
};

const Page: NextPage = ({ headerData, data }: any) => {
  const { basketItem } = useBasketContext();
  const { postcodeItem } = usePostcodeContext();
  const { page, location, rows, communityPosts, blogPosts } = data;

  if (!headerData || !page || !location || !location.display) {
    return <Error statusCode={404} />;
  }

  const { communityPostsTitle } = page;

  const combinedPosts = [...blogPosts, ...communityPosts];
  combinedPosts.sort((a, b) => (a.published_at < b.published_at ? 1 : -1));

  const uniquePosts = Array.from(new Map(combinedPosts.map((post) => [post.id, post])).values());

  const ComponentAttributeMap: {
    [key: string]: any;
  } = {
    coverageSingleHero() {
      return {
        image: location?.image,
        imageMobile: location?.imageMobile,
      };
    },
    choosePackage(props) {
      return {
        preChoosePackagePromoCode: location?.promoCode
          ? `${location?.promoCode}${props.preChoosePackagePromoAmount}`
          : null,
      };
    },
    reviews() {
      return {
        headingStyles: reviewHeadingStyles,
      };
    },
  };

  const components = rows?.map(({ type, value }: any) => {
    const additionalAttributes = ComponentAttributeMap[type]
      ? ComponentAttributeMap[type](value)
      : {};

    const Component: ReactElement<any, any> | null = rowFactory.createRow({
      type,
      attributes: {
        ...value,
        ...additionalAttributes,
        ...data.productSettings,
      },
      products: data?.products,
    });

    return Component;
  });

  // Disable OTS check
  const trueCallback = () => (!postcodeItem ? '/address-finder' : '/service-property-confirmation');
  const falseCallback = () => (!postcodeItem ? '/address-finder' : '/ots');

  const primaryHref = handleConditionalExecution(
    process.env.NEXT_PUBLIC_DISABLE_OTS === 'true',
    trueCallback,
    falseCallback,
  )();

  return (
    <>
      {renderComponents({ components })}
      {uniquePosts && !!uniquePosts.length && (
        <ArchiveGrid
          blogs={uniquePosts}
          categories={[]}
          title={communityPostsTitle}
          theme="transparent"
          postType="combined"
        />
      )}
      {basketItem && (
        <StickyCheckoutFooter
          primaryText={'Proceed'}
          primaryHref={primaryHref}
          show={true}
          isAlwaysOnScreen={true}
          isSticky={true}
        />
      )}
    </>
  );
};

export default Page;
